// MeetControlBar — bottom strip with mic / cam / leave controls. The live
// LiveKit Room is fetched via getLivekitRoom() from the connectCall module
// (storing the Room on reactive state strips its methods).
//
// Shared button styling:
//  - MeetControlBarIconButton: round icon-button base used by mic/cam/screen/
//    captions/share-link/settings. All circular 'C'x'C' with boxSize 'B' icons.
//  - MeetControlBarTextButton:  rectangular text-button base used by End / Leave.
// Both are registered via key name so `extends: 'MeetControlBarIconButton'`
// resolves through the smbls component registry.
import { getLivekitRoom } from '../../functions/meet/connectCall.js'
import { getSDK } from '../../../shared/functions/sdk.js'

const _findRoom = () => getLivekitRoom()

export const MeetControlBarIconButton = {
  tag: 'button',
  padding: 'Z',
  round: '100%',
  border: 'none',
  cursor: 'pointer',
  fontSize: 'A',
  width: 'C',
  height: 'C',
  align: 'center center',
  theme: 'secondary'
}

export const MeetControlBarTextButton = {
  tag: 'button',
  padding: 'Z B',
  round: 'Z',
  border: 'none',
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: '600'
}

export const MeetControlBar = {
  flow: 'x',
  align: 'center center',
  gap: 'A',
  padding: 'A',
  borderTopWidth: '1px',
  borderTopColor: 'line',
  borderTopStyle: 'solid',
  background: 'overlayStrong',

  MicBtn: {
    extends: 'MeetControlBarIconButton',
    theme: (el, s) => {
      const me = (s.root?.livekitParticipants || []).find((p) => p.isLocal)
      return me?.isMicEnabled ? 'secondary' : 'tertiary'
    },
    Icon: {
      icon: (el, s) => {
        const me = (s.root?.livekitParticipants || []).find((p) => p.isLocal)
        return me?.isMicEnabled ? 'mic' : 'micOff'
      }
    },
    title: 'Toggle mic',
    onClick: async (e, el, s) => {
      const room = _findRoom()
      if (!room) return
      const enabled = room.localParticipant.isMicrophoneEnabled
      await room.localParticipant.setMicrophoneEnabled(!enabled)
    }
  },

  CamBtn: {
    extends: 'MeetControlBarIconButton',
    theme: (el, s) => {
      const me = (s.root?.livekitParticipants || []).find((p) => p.isLocal)
      return me?.isCameraEnabled ? 'secondary' : 'tertiary'
    },
    Icon: {
      icon: (el, s) => {
        const me = (s.root?.livekitParticipants || []).find((p) => p.isLocal)
        return me?.isCameraEnabled ? 'video' : 'videoOff'
      }
    },
    title: 'Toggle camera',
    onClick: async (e, el, s) => {
      const room = _findRoom()
      if (!room) return
      const enabled = room.localParticipant.isCameraEnabled
      await room.localParticipant.setCameraEnabled(!enabled)
    }
  },

  ScreenBtn: {
    extends: 'MeetControlBarIconButton',
    Icon: { icon: 'shareScreen' },
    title: 'Share screen',
    onClick: async (e, el, s) => {
      const room = _findRoom()
      if (!room) return
      try {
        const enabled = room.localParticipant.isScreenShareEnabled
        await room.localParticipant.setScreenShareEnabled(!enabled)
      } catch (err) {
        console.warn('[MeetControlBar] screen share failed', err)
      }
    }
  },

  // Show/hide the live captions overlay. Only rendered when the room is
  // transcribed. Default state is "on" — initialized by pages/meet.js.
  CaptionsBtn: {
    extends: 'MeetControlBarIconButton',
    hide: (el, s) => {
      const room = (s.root?.rooms || []).find(
        (r) => r.id === s.root?.activeRoomId
      )
      return !room?.transcribeEnabled
    },
    theme: (el, s) =>
      s.root?.showLiveCaptions === false ? 'tertiary' : 'secondary',
    Icon: { icon: 'closedCaption' },
    title: 'Toggle captions',
    onClick: (e, el, s) => {
      s.root?.update({
        showLiveCaptions: !(s.root?.showLiveCaptions !== false)
      })
    }
  },

  // Share-link button — only for public rooms, copies the /meet-guest link
  // to the clipboard so the host can send it to external attendees.
  ShareLinkBtn: {
    extends: 'MeetControlBarIconButton',
    hide: (el, s) => {
      const room = (s.root?.rooms || []).find(
        (r) => r.id === s.root?.activeRoomId
      )
      return !room || room.isPrivate
    },
    Icon: {
      icon: (el, s) => (s.root?.shareLinkCopied ? 'checkmark' : 'link')
    },
    title: 'Copy guest link',
    onClick: async (e, el, s) => {
      const id = s.root?.activeRoomId
      if (!id || typeof window === 'undefined') return
      const url = `${window.location.origin}/meet-guest#room=${id}`
      try {
        await navigator.clipboard.writeText(url)
        s.root?.update({ shareLinkCopied: true })
        setTimeout(() => s.root?.update({ shareLinkCopied: false }), 1500)
      } catch (err) {
        console.warn('[MeetControlBar] clipboard write failed', err)
      }
    }
  },

  // Open the room settings modal. Owner-only — non-owners can't modify
  // guest requirements. RLS enforces the write on the server anyway.
  SettingsBtn: {
    extends: 'MeetControlBarIconButton',
    hide: (el, s) => {
      const room = (s.root?.rooms || []).find(
        (r) => r.id === s.root?.activeRoomId
      )
      if (!room) return true
      if (room.createdBy && room.createdBy !== s.root?.currentUserId)
        return true
      return false
    },
    Icon: { icon: 'settings' },
    title: 'Room settings',
    onClick: (e, el, s) => s.root?.update({ showRoomSettings: true })
  },

  // End room — owner-only, distinct from Leave. "Leave" disconnects MY
  // local session but the room stays open for others. "End room" marks
  // the DB row ended_at so it drops out of the active list for everyone
  // (others still physically connected to LiveKit stay until they
  // individually disconnect — LiveKit doesn't auto-kick on ended_at;
  // that's a future enhancement via RoomServiceClient.deleteRoom).
  EndRoomBtn: {
    extends: 'MeetControlBarTextButton',
    hide: (el, s) => {
      const room = (s.root?.rooms || []).find(
        (r) => r.id === s.root?.activeRoomId
      )
      if (!room) return true
      if (room.endedAt) return true
      // Owner-only. RLS enforces the write, but hide the button so non-owners
      // don't see a disabled-looking control.
      if (room.createdBy && room.createdBy !== s.root?.currentUserId)
        return true
      return false
    },
    border: null,
    borderWidth: '1px',
    borderColor: 'red',
    borderStyle: 'solid',
    background: 'transparent',
    color: 'red',
    text: 'End room',
    title: 'Mark the room as ended (owner only)',
    onClick: async (e, el, s) => {
      const activeId = s.root?.activeRoomId
      const isTranscribed = (s.root?.rooms || []).find(
        (r) => r.id === activeId
      )?.transcribeEnabled
      // 1) Mark the row ended in the DB (visible across the org via realtime)
      await el.call('endRoom', activeId)
      // 2) Disconnect my own LiveKit session
      await el.call('disconnectCall')
      // 3) Route to transcript if there was transcription, otherwise back
      //    to the room list.
      s.root?.update({ meetView: isTranscribed ? 'transcript' : 'list' })
      if (typeof window !== 'undefined') {
        history.replaceState(
          null,
          '',
          isTranscribed ? `#room=${activeId}&view=transcript` : ''
        )
      }
    }
  },

  LeaveBtn: {
    extends: 'MeetControlBarTextButton',
    background: 'red',
    color: 'white',
    text: 'Leave',
    onClick: async (e, el, s) => {
      const activeId = s.root?.activeRoomId
      const isTranscribed = (s.root?.rooms || []).find(
        (r) => r.id === activeId
      )?.transcribeEnabled
      await el.call('disconnectCall')
      s.root?.update({
        meetView: isTranscribed ? 'transcript' : 'list',
        analyzing: isTranscribed
      })
      if (typeof window !== 'undefined') {
        history.replaceState(
          null,
          '',
          isTranscribed ? `#room=${activeId}&view=transcript` : ''
        )
      }
      if (isTranscribed) {
        // Fetch transcript utterances + fire analysis, populate root state
        // directly (we can't rely on realtime subscription scope because
        // subscribeMeetRealtime scope isn't re-evaluated on view transitions).
        // migrated 2026-04-27 to SDK route workspaceProject.meet.utterances
        try {
          const utterances = await getSDK().execute(
            'workspaceProject.meet.utterances', 'list', { roomId: activeId })
          s.root?.update({
            utterances: (utterances || []).map((r) => ({
              id: r.id,
              roomId: r.room_id,
              userId: r.user_id,
              speakerName: r.speaker_name || '',
              text: r.text || '',
              startedAt: r.started_at,
              endedAt: r.ended_at,
              confidence: r.confidence
            }))
          })
          const analysis = await el.call('analyzeTranscript', activeId)
          s.root?.update({ analyzing: false, analysis })
        } catch (err) {
          console.error('[ControlBar.Leave] analysis load failed', err)
          s.root?.update({
            analyzing: false,
            analyzeError: err?.message || 'analysis failed'
          })
        }
      }
    }
  }
}
